import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from "react-helmet-async";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store } from "./store/store";
import { getProfile } from "./store/slices/authSlice";

// Layout Components
import { Navbar } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { SidebarLayout } from "./components/layout/SidebarLayout";
import { ScrollToTop } from "./components/ui/ScrollToTop";

// Auth Components
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import { PublicRoute } from "./components/auth/PublicRoute";

// Pages
import { HomePage } from "./pages/HomePage";
import { AccountsPage } from "./pages/AccountsPage";
import { AccountDetailsPage } from "./pages/AccountDetailsPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ProfilePage } from "./pages/ProfilePage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { TransactionsPage } from "./pages/TransactionsPage";
import { OrdersPage } from "./pages/OrdersPage";
import { DepositPage } from "./pages/DepositPage";
import { PaymentCallbackPage } from "./pages/PaymentCallbackPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "./pages/TermsOfServicePage";
import { AboutPage } from "./pages/AboutPage";

// Admin Pages
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { AdminAccountsPage } from "./pages/admin/AdminAccountsPage";
import { AdminTransactionsPage } from "./pages/admin/AdminTransactionsPage";
import { AdminVirtualNumberPurchasesPage } from "./pages/admin/AdminVirtualNumberPurchasesPage";
import { AdminSettingsPage } from "./pages/admin/AdminSettingsPage";
import { AdminManualAccountsPage } from "./pages/admin/AdminManualAccountsPage";
import { AdminManualDepositsPage } from "./pages/admin/AdminManualDepositsPage";
import { AdminVirtualNumbersPage } from "./pages/admin/AdminVirtualNumbersPage";
import { AdminPaymentConfigPage } from "./pages/admin/AdminPaymentConfigPage";
import { AdminAnalyticsPage } from "./pages/admin/AdminAnalyticsPage";

// Other Pages
import { NotFoundPage } from "./pages/NotFoundPage";
import { VirtualNumbersPage } from "./pages/VirtualNumbersPage";
import { MyNumbersPage } from "./pages/MyNumbersPage";

// Background Animation Component
import { BackgroundAnimation } from "./components/ui/BackgroundAnimation";
import CelebUpdates from "./pages/CelebUpdates";
import AdminCelebUpdates from "./pages/admin/AdminCelebUpdates";

function AnimatedRoutes() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getProfile());
    }
  }, [dispatch, isAuthenticated, location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public Routes - with Navbar and Footer */}
        <Route
          path="/"
          element={
            <>
              <Navbar />
              <main className="relative z-10 pt-5 sm:pt-5">
                <HomePage />
              </main>
              <Footer />
            </>
          }
        />
        <Route
          path="/privacy"
          element={
            <>
              <Navbar />
              <main className="relative z-10 pt-16 sm:pt-20">
                <PrivacyPolicyPage />
              </main>
              <Footer />
            </>
          }
        />
        <Route
          path="/terms"
          element={
            <>
              <Navbar />
              <main className="relative z-10 pt-16 sm:pt-20">
                <TermsOfServicePage />
              </main>
              <Footer />
            </>
          }
        />
        <Route
          path="/about"
          element={
            <>
              <Navbar />
              <main className="relative z-10 pt-16 sm:pt-20">
                <AboutPage />
              </main>
              <Footer />
            </>
          }
        />

        {/* Auth Routes - Only accessible when NOT logged in */}
        <Route element={<PublicRoute />}>
          <Route
            path="/login"
            element={
              <>
                <Navbar />
                <main className="relative z-10 pt-16 sm:pt-20">
                  <LoginPage />
                </main>
                <Footer />
              </>
            }
          />
          <Route
            path="/register"
            element={
              <>
                <Navbar />
                <main className="relative z-10 pt-16 sm:pt-20">
                  <RegisterPage />
                </main>
                <Footer />
              </>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <>
                <Navbar />
                <main className="relative z-10 pt-16 sm:pt-20">
                  <ForgotPasswordPage />
                </main>
                <Footer />
              </>
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              <>
                <Navbar />
                <main className="relative z-10 pt-16 sm:pt-20">
                  <ResetPasswordPage />
                </main>
                <Footer />
              </>
            }
          />
        </Route>

        {/* Protected User Routes - with Sidebar */}
        <Route element={<ProtectedRoute />}>
          <Route element={<SidebarLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/checkout/:id" element={<CheckoutPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/deposit" element={<DepositPage />} />
            <Route path="/payment/callback" element={<PaymentCallbackPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/tools-and-updates" element={<CelebUpdates />} />
            <Route path="/accounts/:name" element={<AccountDetailsPage />} />
            <Route path="/virtual-numbers" element={<VirtualNumbersPage />} />
            <Route path="/my-numbers" element={<MyNumbersPage />} />
            <Route
              path="/social-boost"
              element={<div>Social Boost Page (Coming Soon)</div>}
            />
            <Route
              path="/purchase-number"
              element={<div>Purchase Number Page (Coming Soon)</div>}
            />
          </Route>
        </Route>

        {/* Admin Routes - with Sidebar */}
        <Route element={<AdminRoute />}>
          <Route element={<SidebarLayout />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/accounts" element={<AdminAccountsPage />} />
            <Route
              path="/admin/virtual-numbers"
              element={<AdminVirtualNumbersPage />}
            />
            <Route
              path="/admin/virtual-number-purchases"
              element={<AdminVirtualNumberPurchasesPage />}
            />
            <Route
              path="/admin/tools-and-updates"
              element={<AdminCelebUpdates />}
            />
            <Route
              path="/admin/payment-config"
              element={<AdminPaymentConfigPage />}
            />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            <Route
              path="/admin/transactions"
              element={<AdminTransactionsPage />}
            />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
          </Route>
        </Route>

        {/* 404 Route */}
        <Route
          path="*"
          element={
            <>
              <Navbar />
              <main className="relative z-10 pt-16 sm:pt-20">
                <NotFoundPage />
              </main>
              <Footer />
            </>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Provider store={store}>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen bg-linear-to-br from-luxury-50 via-white to-luxury-100 relative overflow-x-hidden flex flex-col">
            <BackgroundAnimation />
            <div className="flex-1">
              <AnimatedRoutes />
            </div>
            <Toaster
              position="top-right"
              toastOptions={{
                className: "glass border border-white/30 text-luxury-900",
                duration: 4000,
                style: {
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  color: "#212529",
                },
                success: {
                  iconTheme: {
                    primary: "#0ea5e9",
                    secondary: "#ffffff",
                  },
                },
                error: {
                  iconTheme: {
                    primary: "#ef4444",
                    secondary: "#ffffff",
                  },
                },
              }}
            />
          </div>
        </Router>
      </HelmetProvider>
    </Provider>
  );
}

export default App;
